// src/lib/authClient.ts
// Client-side wrapper for the unified /auth edge function
import {
  createSupabaseRequestHeaders,
  getSupabaseCredentials,
} from './supabaseCredentials'

type AuthMethod = 'telegram' | 'widget' | 'email-request' | 'email-verify' | 'email-legacy' | 'refresh' | 'link-email'

type AuthResponse = {
  token: string
  user: {
    id: string
    telegram_id: number | null
    tg_username: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
    email_verified_at: string | null
    auth_method: string | null
  }
  studios: any[]
}

type EmailRequestResponse = {
  sent: boolean
  loginLink?: string
}

type CallAuthOptions = {
  useUserJwt?: boolean
}

/**
 * Шлюз (api.levita.one) при сбое отвечает HTML-страницей, а не JSON. Раньше её текст целиком
 * уезжал в сообщение об ошибке, и пользователь видел «Ошибка аутентификации: <html>…502 Bad
 * Gateway…» — по такому экрану нельзя понять ни что произошло, ни что делать.
 */
function describeNonJsonResponse(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return 'Сервер авторизации временно недоступен. Попробуйте войти ещё раз через минуту.'
  }
  if (status === 429) {
    return 'Слишком много попыток входа подряд. Подождите минуту и повторите.'
  }
  if (status >= 500) {
    return `Сервер авторизации ответил ошибкой (код ${status}). Попробуйте ещё раз позже.`
  }
  return `Не удалось выполнить вход (код ${status}). Попробуйте ещё раз.`
}

export async function callAuth(
  method: 'email-request',
  params: { email: string; invite_token?: string | null },
  options?: CallAuthOptions
): Promise<EmailRequestResponse>
export async function callAuth(method: AuthMethod, params: Record<string, unknown>, options?: CallAuthOptions): Promise<AuthResponse>
export async function callAuth(method: AuthMethod, params: Record<string, unknown>, options?: CallAuthOptions): Promise<AuthResponse | EmailRequestResponse> {
  const { url, apiKey } = getSupabaseCredentials()
  if (!url || !apiKey) throw new Error('Missing Supabase credentials for auth')
  const appToken = localStorage.getItem('auth_token')
  const requiresUserJwt = method === 'link-email' || options?.useUserJwt === true
  const bearer = requiresUserJwt && appToken ? appToken : apiKey
  const headers = createSupabaseRequestHeaders(apiKey, bearer, {
    'content-type': 'application/json',
  })

  const response = await fetch(`${url}/functions/v1/auth`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ method, ...params })
  })

  const responseText = await response.text()
  const contentType = response.headers.get('content-type') || ''
  const parseJsonSafely = () => {
    if (!responseText.trim()) return {}
    if (!contentType.includes('application/json')) {
      throw new Error(describeNonJsonResponse(response.status))
    }
    try {
      return JSON.parse(responseText) as AuthResponse | EmailRequestResponse | { error?: string }
    } catch {
      throw new Error(describeNonJsonResponse(response.status))
    }
  }

  if (!response.ok) {
    const body = parseJsonSafely() as { error?: string }
    throw new Error(body.error || describeNonJsonResponse(response.status))
  }

  return parseJsonSafely() as AuthResponse | EmailRequestResponse
}
